import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, Send, Loader2, Volume2, VolumeX, CheckCircle2, RotateCcw } from 'lucide-react';
import { useHandsFreeMic } from '../voice/useHandsFreeMic';
import { transcribeBlob } from '../voice/transcribe';
import { MicCheck } from '../voice/MicCheck';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });
const NOISE = /^(www\.|https?:|thank you\.?$|thanks for watching|you\.?$|bye\.?$|\.+$)/i;
const GREETING = 'Hands-free mode. Just talk. I can send post-op or pre-op instructions, consents, compliance forms, Med Cards, problem sheets, eCards, Tooth Guides, a message or a referral. For example: "Send post-op instructions for a full denture to Cary Ganz by text and email."';
const KIND = { postop: 'post-op', preop: 'pre-op', consent: 'form', medcard: 'Med Card', problem: 'problem sheet', ecard: 'eCard' };

const Draft = ({ d }) => {
  if (!d || (!d.items?.length && !d.patient)) return null;
  const row = (k, v) => v ? <div className="flex gap-2"><span className="text-slate-400 w-16 shrink-0">{k}</span><span className="text-slate-700">{v}</span></div> : null;
  return (
    <div className="text-xs border border-slate-200 rounded-lg p-2.5 space-y-1 bg-white" data-testid="voice-draft">
      {row('Send', d.items?.map((x) => (KIND[x.kind] ? `${x.name} (${KIND[x.kind]})` : x.name)).join(', '))}
      {row('Message', d.message)}
      {row('Referral', d.referral)}
      {row('Patient', d.patient && `${d.patient}${d.isNew ? ' (new)' : ''}`)}
      {row('Via', d.channels?.map((c) => (c === 'sms' ? 'Text' : 'Email')).join(' + '))}
      {row('Cell', d.phone)}
      {row('Email', d.email)}
    </div>
  );
};

export const DrGVoiceMode = ({ Avatar }) => {
  const [log, setLog] = useState([{ role: 'assistant', text: GREETING }]);
  const [busy, setBusy] = useState('');
  const [speak, setSpeak] = useState(true);
  const [draft, setDraft] = useState(null);
  const [result, setResult] = useState(null);
  const [text, setText] = useState('');
  const sessionRef = useRef(null);
  const speakRef = useRef(true);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const liveRef = useRef(true);
  const mic = useHandsFreeMic((blob) => onBlob(blob));

  useEffect(() => { speakRef.current = speak; }, [speak]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [log, busy]);
  useEffect(() => {
    liveRef.current = true;
    const t = setTimeout(() => mic.start(), 150);
    return () => { liveRef.current = false; clearTimeout(t); audioRef.current?.pause(); mic.stop(); };
  }, []);

  const listenAgain = () => { if (liveRef.current) mic.start(); };
  const play = (b64, done) => {
    if (!b64 || !speakRef.current) { if (!done) listenAgain(); return; }
    audioRef.current?.pause();
    const a = new Audio(`data:audio/mp3;base64,${b64}`);
    audioRef.current = a;
    a.onended = () => { if (!done) listenAgain(); };
    a.play().catch(() => { if (!done) listenAgain(); });
  };

  const sendText = async (t) => {
    if (!t.trim()) return;
    setLog((l) => [...l, { role: 'user', text: t }]);
    setBusy('Thinking…');
    try {
      const r = await axios.post(`${BACKEND}/api/voice-command/turn`, { text: t, sessionId: sessionRef.current, speak: speakRef.current }, auth());
      sessionRef.current = r.data.done ? null : r.data.sessionId;
      setLog((l) => [...l, { role: 'assistant', text: r.data.reply }]);
      setDraft(r.data.done ? null : r.data.draft);
      if (r.data.result) setResult(r.data.result);
      setBusy('');
      play(r.data.audio, r.data.done);
    } catch (e) {
      setBusy('');
      setLog((l) => [...l, { role: 'assistant', text: e.response?.data?.detail || 'Sorry, something went wrong. Please try again.' }]);
    }
  };

  async function onBlob(blob) {
    setBusy('Transcribing…');
    try {
      const said = (await transcribeBlob(blob, 'cmd')).trim();
      setBusy('');
      if (said && !NOISE.test(said)) sendText(said); else listenAgain();
    } catch { setBusy(''); setLog((l) => [...l, { role: 'assistant', text: "I couldn't hear that. Tap the mic and try again." }]); }
  }

  const reset = () => { audioRef.current?.pause(); mic.stop(); setLog([{ role: 'assistant', text: GREETING }]); sessionRef.current = null; setDraft(null); setResult(null); };

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f8fbfd]" data-testid="voice-command-log">
        {log.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role !== 'user' && <Avatar />}
            <p className={`max-w-[85%] text-sm rounded-2xl px-3 py-2 leading-snug ${m.role === 'user' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-800'}`} data-testid={`voice-msg-${m.role}-${i}`}>{m.text}</p>
          </div>
        ))}
        {busy && <p className="pl-10 text-xs text-slate-500 flex items-center gap-1" data-testid="voice-command-busy"><Loader2 className="h-3 w-3 animate-spin" />{busy}</p>}
        {result && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 text-xs text-emerald-900 space-y-0.5" data-testid="voice-command-result">
            <p className="font-semibold flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />Saved to {result.patientName}'s Patient Records</p>
            {result.results.map((r) => <p key={r}>• {r}</p>)}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="px-3"><Draft d={draft} /></div>
      <div className="border-t bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-1.5 sm:gap-2">
        <button onClick={mic.listening ? mic.stop : mic.start} disabled={!!busy}
          className={`h-11 w-11 shrink-0 rounded-full flex items-center justify-center text-white ${mic.listening ? 'bg-rose-600 animate-pulse' : 'bg-sky-600 hover:bg-sky-700'} disabled:opacity-50`}
          data-testid={mic.listening ? 'voice-command-stop-btn' : 'voice-command-mic-btn'} aria-label={mic.listening ? 'Stop listening' : 'Speak'}>
          <Mic className="h-5 w-5" />
        </button>
        <form className="flex-1 flex gap-1 min-w-0" onSubmit={(e) => { e.preventDefault(); mic.stop(); sendText(text); setText(''); }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={mic.listening ? 'Listening… just talk' : 'Or type here…'}
            className="flex-1 min-w-0 border border-slate-200 rounded-full px-3 text-base sm:text-sm h-10 focus:outline-none focus:ring-2 focus:ring-sky-200" data-testid="voice-command-text-input" />
          <button type="submit" disabled={!text.trim() || !!busy} className="h-10 w-10 rounded-full bg-slate-800 text-white flex items-center justify-center disabled:opacity-40" data-testid="voice-command-send-btn"><Send className="h-4 w-4" /></button>
        </form>
        <button onClick={() => setSpeak((s) => !s)} title={speak ? 'Mute Dr. G' : 'Speak replies'} className="p-2 text-slate-500 hover:text-sky-700" data-testid="voice-command-mute-btn">{speak ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}</button>
        <button onClick={reset} title="Start over" className="p-2 text-slate-500 hover:text-sky-700" data-testid="voice-command-reset-btn"><RotateCcw className="h-4 w-4" /></button>
      </div>
      <MicCheck mic={mic} prefix="voice-command" className="px-3 pb-2" />
    </>
  );
};
